import React, { useState, useMemo, useEffect } from 'react';
import { ChevronLeft, ChevronRight, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { TableSkeleton } from '../common/Skeleton';

/**
 * DataTable 컴포넌트
 * 관리자 페이지에서 사용되는 데이터 테이블
 *
 * 하위호환: 기존 props(columns/data/onRowClick/emptyMessage)는 그대로 동작.
 * 추가(옵셔널):
 * @param {boolean} props.loading - true 면 스켈레톤 행 표시
 * @param {number} props.pageSize - 페이지당 행 수 (지정 시 페이지네이션 활성화)
 * @param {boolean} props.selectable - 행 선택 체크박스 표시
 * @param {Array} props.selectedIds - 선택된 행 id 배열
 * @param {Function} props.onSelectChange - 선택 변경 콜백(새 id 배열 전달)
 *
 * 컬럼 정의 확장(옵셔널):
 *   { key, label, sortable, render, sortType: 'string'|'number'|'date', sortValue: (row)=>any }
 */
const DataTable = ({
  columns = [],
  data = [],
  onRowClick,
  emptyMessage = '데이터가 없습니다.',
  loading = false,
  pageSize,
  selectable = false,
  selectedIds = [],
  onSelectChange
}) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [page, setPage] = useState(1);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // 정렬 타입 추론: 컬럼이 sortType 지정 안 했으면 첫 유효 값으로 추론
  const getColumn = (key) => columns.find((c) => c.key === key);

  const getSortValue = (row, column) => {
    if (column?.sortValue) return column.sortValue(row);
    return row[column.key];
  };

  const inferType = (value) => {
    if (value == null) return 'string';
    if (typeof value === 'number') return 'number';
    if (typeof value === 'boolean') return 'number';
    if (typeof value === 'string') {
      // ISO 날짜 또는 일반 날짜 문자열
      if (/^\d{4}-\d{2}-\d{2}/.test(value) && !Number.isNaN(Date.parse(value))) {
        return 'date';
      }
      // 순수 숫자 문자열
      if (value.trim() !== '' && !Number.isNaN(Number(value))) return 'number';
    }
    return 'string';
  };

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return data;
    const column = getColumn(sortConfig.key);
    const sortType =
      column?.sortType ||
      inferType(getSortValue(data.find((r) => getSortValue(r, column) != null) || {}, column));

    const sorted = [...data].sort((a, b) => {
      let aValue = getSortValue(a, column);
      let bValue = getSortValue(b, column);

      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      let comparison;
      if (sortType === 'number') {
        comparison = Number(aValue) - Number(bValue);
      } else if (sortType === 'date') {
        comparison = new Date(aValue).getTime() - new Date(bValue).getTime();
      } else {
        comparison = String(aValue).localeCompare(String(bValue), 'ko');
      }
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });

    return sorted;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, sortConfig, columns]);

  // 페이지네이션
  const usePaging = typeof pageSize === 'number' && pageSize > 0;
  const totalPages = usePaging ? Math.max(1, Math.ceil(sortedData.length / pageSize)) : 1;

  // 데이터/정렬/필터 변경으로 현재 페이지가 범위를 벗어나면 보정
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const pagedData = usePaging
    ? sortedData.slice((page - 1) * pageSize, page * pageSize)
    : sortedData;

  const renderSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <ChevronsUpDown className="ml-1 inline h-3.5 w-3.5 text-gray-400" />;
    }
    return sortConfig.direction === 'asc' ? (
      <ChevronUp className="ml-1 inline h-3.5 w-3.5 text-primary-600" />
    ) : (
      <ChevronDown className="ml-1 inline h-3.5 w-3.5 text-primary-600" />
    );
  };

  const ariaSortFor = (columnKey) => {
    if (sortConfig.key !== columnKey) return 'none';
    return sortConfig.direction === 'asc' ? 'ascending' : 'descending';
  };

  // 선택 로직
  const pageIds = pagedData.map((r) => r.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));

  const toggleAll = () => {
    if (!onSelectChange) return;
    if (allPageSelected) {
      onSelectChange(selectedIds.filter((id) => !pageIds.includes(id)));
    } else {
      onSelectChange(Array.from(new Set([...selectedIds, ...pageIds])));
    }
  };

  const toggleOne = (id) => {
    if (!onSelectChange) return;
    if (selectedIds.includes(id)) {
      onSelectChange(selectedIds.filter((x) => x !== id));
    } else {
      onSelectChange([...selectedIds, id]);
    }
  };

  const totalColSpan = columns.length + (selectable ? 1 : 0);

  return (
    <div className="overflow-x-auto rounded-lg bg-white shadow">
      <table className="w-full">
        <thead className="border-b-2 border-gray-200 bg-gray-50">
          <tr>
            {selectable && (
              <th className="w-12 px-4 py-3 text-left">
                <input
                  type="checkbox"
                  checked={allPageSelected}
                  onChange={toggleAll}
                  aria-label="현재 페이지 전체 선택"
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
              </th>
            )}
            {columns.map((column) => (
              <th
                key={column.key}
                aria-sort={column.sortable ? ariaSortFor(column.key) : undefined}
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-700 ${
                  column.sortable ? 'cursor-pointer select-none hover:bg-gray-100' : ''
                }`}
                onClick={() => column.sortable && handleSort(column.key)}
              >
                <div className="flex items-center">
                  {column.label}
                  {column.sortable && renderSortIcon(column.key)}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {loading ? (
            <tr>
              <td colSpan={totalColSpan} className="p-0">
                <TableSkeleton rows={pageSize || 5} columns={totalColSpan} />
              </td>
            </tr>
          ) : pagedData.length === 0 ? (
            <tr>
              <td colSpan={totalColSpan} className="px-6 py-8 text-center text-gray-500">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            pagedData.map((row, rowIndex) => {
              const checked = selectedIds.includes(row.id);
              return (
                <tr
                  key={row.id ?? rowIndex}
                  className={`${onRowClick ? 'cursor-pointer hover:bg-gray-50' : ''} ${
                    checked ? 'bg-primary-50' : ''
                  } transition-colors`}
                  onClick={() => onRowClick && onRowClick(row)}
                >
                  {selectable && (
                    <td className="w-12 px-4 py-4" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleOne(row.id)}
                        aria-label="행 선택"
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                    </td>
                  )}
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className="whitespace-nowrap px-6 py-4 text-sm text-gray-900"
                    >
                      {column.render ? column.render(row[column.key], row) : row[column.key]}
                    </td>
                  ))}
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      {/* 페이지네이션 */}
      {usePaging && !loading && sortedData.length > 0 && (
        <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 text-sm">
          <span className="text-gray-600">
            {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, sortedData.length)} / 총{' '}
            {sortedData.length}개
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              aria-label="이전 페이지"
              className="rounded-md p-1.5 text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="px-2 text-gray-700">
              {page} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              aria-label="다음 페이지"
              className="rounded-md p-1.5 text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;
